import { Dialog, DialogTrigger } from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { Popover } from '@/lib/base/overlays/popover';
import { cx } from '@/utils/cx';

import { calendarDayKey } from '../utils/calendarDays';
import { isEventPast } from '../utils/calendarUtils';
import type { CalendarEvent } from '../utils/eventMapper';
import { MonthEventChip } from './MonthEventChip';
import { MonthInlineEventRow } from './MonthInlineEventRow';

/** Max all-day lanes/rows shown per day before the rest collapse into a "+N more" popover. */
export const MAX_VISIBLE_ALL_DAY_LANES = 3;

// Panel chrome + scroll live on the lib/base Popover (which also owns
// outside-click dismissal); its `md` size caps the panel at max-h-80. `py-0`
// drops the wrapper's list padding (the header/body pad themselves) and
// `w-auto min-w-64` replace its trigger-width sizing.
const DAY_EVENTS_POPOVER_CLASS = 'w-auto min-w-64 max-w-72 py-0 shadow-xl';

/**
 * Compute the chip's `spanPosition` for an all-day event RELATIVE to a
 * given day. Mirrors the calendar's per-row span logic so the popover
 * uses the same continuation-edge cues:
 *  - 'start'  → event starts in this day, extends to the next day
 *  - 'end'    → event started before this day, ends in this day
 *  - 'middle' → event spans through (started before, extends after)
 *  - undefined → single-day event (no continuation)
 *
 * All-day events have `start_time = '<startDate>T00:00:00'` and
 * `end_time = '<endDate>T23:59:59'`, so we just compare the YMD
 * prefixes against the popover day's profile-timezone YYYY-MM-DD.
 */
function spanPositionForDay(
  event: CalendarEvent,
  dayYmd: string
): 'start' | 'middle' | 'end' | undefined {
  const startYmd = event.start_time.split('T')[0];
  const endYmd = event.end_time.split('T')[0];
  const startsBefore = startYmd < dayYmd;
  const endsAfter = endYmd > dayYmd;
  if (startsBefore && endsAfter) {
    return 'middle';
  }
  if (startsBefore) {
    return 'end';
  }
  if (endsAfter) {
    return 'start';
  }
  return undefined;
}

/**
 * Day-detail popover content. Lists every event for the day:
 *  - All-day events as `MonthEventChip` (full-width colored bar) with
 *    chevron continuation cues when the event extends past the day.
 *  - Timed events as `MonthInlineEventRow` (dot + time + title).
 * Rendered inside a react-aria Popover/Dialog anchored to the "+N more"
 * trigger.
 */
export function DayEventsPopover({
  date,
  timezone,
  events,
  onEventClick,
}: {
  /** The calendar day this popover lists (profile-tz midnight) */
  date: Date;
  /** IANA timezone (profile timezone) `date` is a calendar day in */
  timezone: string;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}) {
  const fullDateLabel = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
  const dayYmd = calendarDayKey(date, timezone);
  return (
    <Popover
      size='md'
      placement='right top'
      offset={4}
      shouldFlip
      containerPadding={16}
      className={DAY_EVENTS_POPOVER_CLASS}
    >
      <Dialog
        aria-label={`Events for ${fullDateLabel}`}
        className='outline-hidden'
      >
        <div className='border-b border-secondary px-3 py-2'>
          <h3 className='text-sm font-semibold text-primary'>
            {fullDateLabel}
          </h3>
          <p className='text-xs text-tertiary'>
            {events.length} event{events.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className='space-y-0.5 p-2'>
          {events.map(event => {
            if (event.is_all_day) {
              const spanPosition = spanPositionForDay(event, dayYmd);
              return (
                <MonthEventChip
                  key={event.id}
                  timezone={timezone}
                  event={event}
                  onClick={onEventClick}
                  isPast={isEventPast(event)}
                  isMultiDay={spanPosition !== undefined}
                  spanPosition={spanPosition}
                />
              );
            }
            return (
              <MonthInlineEventRow
                key={event.id}
                timezone={timezone}
                event={event}
                onClick={onEventClick}
                isPast={isEventPast(event)}
              />
            );
          })}
        </div>
      </Dialog>
    </Popover>
  );
}

/**
 * Self-contained "+N more" trigger that opens `DayEventsPopover`. Used by
 * WeekView and DayView. Safe to place inside a clickable container — the
 * button stops propagation before the click can bubble up.
 */
export function MoreEventsPopover({
  date,
  timezone,
  events,
  count,
  onEventClick,
  className,
}: {
  /** The calendar day this trigger belongs to (profile-tz midnight) */
  date: Date;
  /** IANA timezone (profile timezone) `date` is a calendar day in */
  timezone: string;
  /** Full event list for the day (all-day + timed), in display order. */
  events: CalendarEvent[];
  /** The N shown in "+N more". */
  count: number;
  onEventClick?: (event: CalendarEvent) => void;
  /** Extra classes for the trigger button (placement per view). */
  className?: string;
}) {
  return (
    <DialogTrigger>
      <Button
        color='tertiary'
        size='sm'
        className={cx(
          '!justify-start !px-1.5 !py-0.5 !text-xs !font-medium',
          className
        )}
        aria-label={`Show ${count} more events for ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: timezone })}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        +{count} more
      </Button>
      <DayEventsPopover
        date={date}
        timezone={timezone}
        events={events}
        onEventClick={onEventClick}
      />
    </DialogTrigger>
  );
}
